import { InvoicePrintView } from '../features/invoices/InvoicePrintView';
import { SettlementPrintView } from '../features/invoices/SettlementPrintView';
import { UnavailablePage } from '../components/ui/UnavailablePage';
import { Navigate, createBrowserRouter, createHashRouter } from 'react-router-dom';
import { isDemo } from '../demo/flag';
import { AppShell } from './AppShell';
import { LoginPage } from '../auth/LoginPage';
import { AuthCallbackPage } from '../auth/AuthCallbackPage';
import { RequireAuth } from '../auth/RequireAuth';
import { DashboardView } from '../features/dashboard/DashboardView';
import { AnalyticsView } from '../features/analytics/AnalyticsView';
import { OrdersView } from '../features/orders/OrdersView';
import { ShipmentsView } from '../features/shipments/ShipmentsView';
import { ShipmentDetailView } from '../features/shipments/ShipmentDetailView';
import { StoppedTrucksView } from '../features/stopped/StoppedTrucksView';
import { DocumentsView } from '../features/documents/DocumentsView';
import { InterestsView } from '../features/interests/InterestsView';
import { UsersView } from '../features/users/UsersView';
import { AccountsView } from '../features/accounts/AccountsView';
import { UserProfileView } from '../features/users/UserProfileView';
import { ServiceLanesView } from '../features/service-lanes/ServiceLanesView';
import { SettingsView } from '../features/settings/SettingsView';
import { MyWorkView } from '../features/work/MyWorkView';
import { UnassignedView } from '../features/work/UnassignedView';
import { PaymentsView } from '../features/payments/PaymentsView';
import { PublicTrackingPage } from '../features/tracking/PublicTrackingPage';
import { TodayView } from '../features/today/TodayView';
import { ProfitView } from '../features/profit/ProfitView';
import { HelpPage } from '../help/HelpPage';

/**
 * URL-addressable routes (unlike the prototype's view-state switching) so ops
 * can deep-link shipments/queues. Detail routes (e.g. /shipments/:id) land in
 * Phase 1.
 */
/**
 * Demo previews are static files under a path prefix (/demo/pr-N/) with no
 * server-side rewrite, so they use hash routing; the real console keeps
 * clean URLs.
 */
const createRouter = isDemo() ? createHashRouter : createBrowserRouter;

export const router = createRouter([
  { path: '/invoices/:invoiceId/print', element: <RequireAuth><InvoicePrintView /></RequireAuth>, errorElement: <UnavailablePage /> },
  { path: '/shipments/:id/settlement/print', element: <RequireAuth><SettlementPrintView /></RequireAuth>, errorElement: <UnavailablePage /> },
  { path: '/login', element: <LoginPage />, errorElement: <UnavailablePage /> },
  // Public tracking (#294): no login. Demo world only until rhino-api ships GET /public/track/{code}
  // (dnm-work#150); outside demo the route is not registered and the RequireAuth tree answers.
  ...(isDemo()
    ? [{ path: '/track/:code', element: <PublicTrackingPage />, errorElement: <UnavailablePage /> }]
    : []),
  { path: '/auth/callback', element: <AuthCallbackPage />, errorElement: <UnavailablePage /> },
  {
    path: '/',
    errorElement: <UnavailablePage />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: "*", element: <UnavailablePage />, errorElement: <UnavailablePage /> },
      { index: true, element: <DashboardView />, errorElement: <UnavailablePage /> },
      // Today board (#299): demo world only until rhino-api ships GET /admin/work/today (dnm-work#151);
      // outside demo the route is not registered and the "*" route answers.
      ...(isDemo() ? [{ path: 'today', element: <TodayView />, errorElement: <UnavailablePage /> }] : []),
      { path: 'work', element: <MyWorkView />, errorElement: <UnavailablePage /> },
      { path: 'unassigned', element: <UnassignedView />, errorElement: <UnavailablePage /> },
      { path: 'payments', element: <PaymentsView />, errorElement: <UnavailablePage /> },
      { path: 'analytics', element: <AnalyticsView />, errorElement: <UnavailablePage /> },
      // Profit views (#300): demo world only until rhino-api ships /admin/profit/* (dnm-work#154); manager only.
      ...(isDemo() ? [{ path: 'profit', element: <ProfitView />, errorElement: <UnavailablePage /> }] : []),
      { path: 'orders', element: <OrdersView />, errorElement: <UnavailablePage /> },
      { path: 'shipments', element: <ShipmentsView />, errorElement: <UnavailablePage /> },
      { path: 'shipments/:id', element: <ShipmentDetailView />, errorElement: <UnavailablePage /> },
      { path: 'stopped', element: <StoppedTrucksView />, errorElement: <UnavailablePage /> },
      // The old Exceptions page (dnm-work#165 C6): bookmarks land on Stopped trucks.
      { path: 'exceptions', element: <Navigate to="/stopped" replace />, errorElement: <UnavailablePage /> },
      { path: 'documents', element: <DocumentsView />, errorElement: <UnavailablePage /> },
      { path: 'interests', element: <InterestsView />, errorElement: <UnavailablePage /> },
      { path: 'drivers', element: <UsersView role="DRIVER" />, errorElement: <UnavailablePage /> },
      { path: 'shippers', element: <UsersView role="SHIPPER" />, errorElement: <UnavailablePage /> },
      { path: 'fleets', element: <UsersView role="FLEET_MANAGER" />, errorElement: <UnavailablePage /> },
      { path: 'accounts', element: <AccountsView />, errorElement: <UnavailablePage /> },
      { path: 'service-lanes', element: <ServiceLanesView />, errorElement: <UnavailablePage /> },
      { path: 'users/:id', element: <UserProfileView />, errorElement: <UnavailablePage /> },
      { path: 'help', element: <HelpPage />, errorElement: <UnavailablePage /> },
      { path: 'help/:sectionId', element: <HelpPage />, errorElement: <UnavailablePage /> },
      { path: 'settings', element: <SettingsView />, errorElement: <UnavailablePage /> },
      // Dashboard "Review lane cap" card opens here (#297, demo only; outside demo it is plain Settings).
      { path: 'settings/approval-limits', element: <SettingsView initialTab="approvals" />, errorElement: <UnavailablePage /> },
    ],
  },
]);
